import { fn } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';

import type { PopupData, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { UserSettingsKey } from 'webapp/enums/user-settings';
import { uniq } from 'webapp/utils/common';

import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import CommonAskToConnectButtonComponent from 'webapp/components/common/ask-to-connect-button';
import CommonSecurityInfoComponent from 'webapp/components/common/security-info';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type ConnectSelection from 'webapp/models-custom/connect-selection';

export default class DataManagerPopupSelectedConnectionsComponent extends PopupInnerModalComponent<PopupData.None> {
	@service('user-settings') declare userSettingsService: Service.UserSettings;
	@service('app-notifications') declare appNotificationService: Service.AppNotifications;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('flow/tour/onboarding')
	declare flowtTourOnboardingService: Service.Flow.Tour.Onboarding;

	popupTitle = 'These tools are awaiting your action';
	popupDescription = 'The list comprises tools you selected during the account creation process.';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.analyticsService.event(TrackingEvent.CONNECT_SELECTED_INTEGRATIONS_POPUP_SHOWN, {
			datasources: uniq(this.connectSelections.map((m) => m.sourceType?.key)),
		});

		next(() => {
			void this.userSettingsService.save(
				UserSettingsKey.SELECTED_CONNECTION_POPUP_SEEN,
				true,
			);
		});
	}

	get connectSelections() {
		return this.appNotificationService.connectSelections;
	}

	@action
	connect(model: ConnectSelection) {
		const sourceType = model.sourceType;

		const afterConnect = () => {
			this.close();
		};

		if (this.flowtTourOnboardingService.isBannerShown) {
			this.flowtTourOnboardingService.activeTour?.hideBubble();
		}

		if (sourceType?.isGroup) {
			this.mmSourceTypesService.openSourceTypeInfoPopup(sourceType, (sourceType) => {
				void sourceType.connectNew().then(afterConnect);
			});
		} else {
			void sourceType?.connectNew().then(afterConnect);
		}
	}
	<template>
		<div class="content">
			<div class="sides-ct has-wide-left-side">
				<div class="side side-left">
					<div class="side-left__content">
						<img
							src="/images/svg/selected-connections.svg"
							width="auto"
							height="64"
							alt
						/>
						<h2>{{{this.popupTitle}}}</h2>
						<p>{{this.popupDescription}}</p>
					</div>

					<CommonSecurityInfoComponent />
				</div>
				<div class="side side-right">

					<h2>{{{this.popupTitle}}}</h2>
					{{! shown only on mobile when side-left hidden }}
					<p>{{this.popupDescription}}</p>
					{{! shown only on mobile when side-left hidden }}

					<p>Connect all your data to one place</p>
					<ul class="sources-map">
						{{#each this.connectSelections as |model|}}
							<li class="t1">
								<AvatarOrInitialsComponent
									@name={{model.sourceType.name}}
									@avatar={{model.sourceType.avatar}}
								/>
								<span class="sourceTitle">{{model.sourceType.name}}</span>
								<div class="actions">
									{{#if model.sourceType}}
										<CommonAskToConnectButtonComponent
											@model={{model.sourceType}}
										/>
									{{/if}}
									<SubmitButtonComponent
										@text="Connect"
										@icon="Connect"
										@noDelay={{true}}
										@onTrigger={{fn this.connect model}}
										@theme="green"
									/>
								</div>
							</li>
						{{/each}}
					</ul>
				</div>
			</div>
		</div>
	</template>
}
